































































































import type { Lesson } from '@/models/lesson';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { lessonWebservice } from '@/webservices/lesson';
import { xLessonWebservice } from '@/webservices/xlesson';
import { pmLessonWebservice } from '@/webservices/pmlesson';
import type { User } from '@/models/user';
import { flattenSubLessons } from '@/helpers/flattenSubLessons';
import * as process from 'node:process';
import { hasPermission } from '@/helpers/auth/permissionHelpers';
import { PERMISSIONS } from '@/helpers/auth/permissions';
import { resolveLessonTypeByLesson } from '@/helpers/resolveLessonType';

@Component({
    name: 'LessonContainer',
    computed: {
        PERMISSIONS() {
            return PERMISSIONS;
        },
    },
    methods: { hasPermission },
})
export default class LessonContainer extends Vue {
    @Prop() private lesson: any;
    @Prop() private checkedParentLesson: Lesson[];

    @Prop() private parentCheckBox: boolean;
    @Prop() private editButton: boolean;
    @Prop() private details: boolean;
    @Prop() private moreInfoButton: boolean;
    @Prop() private checkbox: boolean;
    @Prop() private orderButton: boolean;
    @Prop() private value: Lesson[];
    @Prop({ default: false }) private isFirstLesson: boolean;
    @Prop({ default: false }) private isLastLesson: boolean;
    @Prop({ default: null }) private disableLesson: Lesson | null;

    @Getter('account/userInfo') private userInfo: User;

    private updatingOrder: boolean = false;

    private parentLessonChange() {
        console.log('input event parentLessonChange');
        if (this.isCurrentLessonSelectedAsParent) {
            const {
                sub_lessons,
                topic,
                topics,
                user,
                scope,
                project,
                sub_phase,
                x_sub_process,
                tags,
                ...rest
            } = this.lesson;
            this.$emit('selectedParentLesson', [rest]);
        } else {
            this.$emit('selectedParentLesson', []);
        }
    }

    get isOrderAllowed() {
        return (
            hasPermission(
                resolveLessonTypeByLesson(this.lesson, {
                    x: PERMISSIONS.APPEX_X_LESSON_EDIT_ORDER,
                    pm: PERMISSIONS.APPEX_PM_LESSON_EDIT_ORDER,
                    trade: PERMISSIONS.APPEX_LESSON_EDIT_ORDER,
                })
            ) && this.orderButton
        );
    }

    get canEdit() {
        const hasOwnEditPermission = hasPermission(
            resolveLessonTypeByLesson(this.lesson, {
                x: PERMISSIONS.APPEX_X_LESSON_EDIT,
                pm: PERMISSIONS.APPEX_PM_LESSON_EDIT,
                trade: PERMISSIONS.APPEX_LESSON_EDIT,
            })
        );

        const hasGlobalEditPermission = hasPermission(
            resolveLessonTypeByLesson(this.lesson, {
                x: PERMISSIONS.APPEX_X_LESSON_EDIT_ALL,
                pm: PERMISSIONS.APPEX_PM_LESSON_EDIT_ALL,
                trade: PERMISSIONS.APPEX_LESSON_EDIT_ALL,
            })
        );

        const canEdit =
            (hasOwnEditPermission && this.lesson.user_id === this.userInfo.id && !this.lesson.approved) ||
            hasGlobalEditPermission;

        return canEdit && this.editButton;
    }

    get isParentCheckboxDisabled() {
        let adjustedMaxLevel = 0;

        if (this.disableLesson && this.disableLesson.id === this.lesson.id) {
            return true;
        }

        if (this.disableLesson && this.disableLesson.hasOwnProperty('sub_lessons')) {
            const subLessons = flattenSubLessons(this.disableLesson).filter((l) => l.hasOwnProperty('level'));
            adjustedMaxLevel = Math.max(...subLessons.map((l) => l.level), 0) - this.disableLesson.level;

            if (subLessons.some((l) => l.id === this.lesson.id)) {
                return true;
            }
        }

        return adjustedMaxLevel + this.lesson.level >= Number(Vue.prototype.LESSON_MAX_LEVEL);
    }

    get lessonWithOutSubs() {
        const {
            sub_lessons,
            topic,
            topics,
            user,
            scope,
            project,
            sub_phase,
            x_sub_process,
            tags,
            ...rest
        } = this.lesson;
        return rest;
    }

    private checkedLessonsChange() {
        const subLessons = this.getSubLessonsRec(this.lesson.sub_lessons, [], !this.isCurrentLessonSelected);

        if (this.isCurrentLessonSelected) {
            // add all sub lessons to checked lesson
            this.$emit('input', this.value.concat(subLessons));
        } else {
            // removes all sub lessons from checked lesson
            this.$emit(
                'input',
                this.value.filter((lesson) => !subLessons.includes(lesson) || lesson.mandatory)
            );
        }
    }

    get isCurrentLessonSelectedAsParent() {
        return this.isLessonSelected(this.lesson, this.checkedParentLesson);
    }

    get isCurrentLessonSelected() {
        return this.isLessonSelected(this.lesson, this.checkedLessons);
    }

    // noinspection JSUnusedGlobalSymbols
    get isChildSelected() {
        let subLessonSelected = false;
        for (let index = 0; index < this.lesson.sub_lessons.length; index++) {
            const lesson = this.lesson.sub_lessons[index];
            subLessonSelected = this.isLessonSelected(lesson, this.checkedLessons);
            if (subLessonSelected) {
                return true;
            }
        }
        return false;
    }

    private isLessonSelected(lesson: Lesson, lessons: Lesson[]) {
        return lessons.findIndex((el) => el.id === lesson.id) !== -1;
    }

    get checkedLessons() {
        return this.value;
    }

    // noinspection JSUnusedGlobalSymbols
    set checkedLessons(lessons: Lesson[]) {
        this.$emit('input', lessons);
    }

    get localParentLesson() {
        return this.checkedParentLesson;
    }

    // noinspection JSUnusedGlobalSymbols
    set localParentLesson(lessons: Lesson[]) {
        this.$emit('selectedParentLesson', lessons);
    }

    private getSubLessonsRec(subLessons: Lesson[], foundLessons: Lesson[], selected: boolean) {
        subLessons.forEach((lesson) => {
            if (this.isLessonSelected(lesson, this.checkedLessons) === selected) {
                foundLessons.push(lesson);
            }
            if (lesson.sub_lessons) {
                this.getSubLessonsRec(lesson.sub_lessons, foundLessons, selected);
            }
        });
        return foundLessons;
    }

    private updateOrder(direction: string = 'up') {
        this.updatingOrder = true;

        let webservice: any = lessonWebservice;

        if (this.lesson.hasOwnProperty('pm_sub_phase_id')) {
            webservice = pmLessonWebservice;
        }
        if (this.lesson.hasOwnProperty('x_sub_process_id')) {
            webservice = xLessonWebservice;
        }

        webservice
            .updateOrder(this.lesson, direction)
            .then(() => {
                this.$buefy.toast.open({
                    duration: 1000,
                    message: this.$t('toastMessages.update.lesson').toString(),
                    type: 'is-success',
                });
                this.$emit('reload');
                this.updatingOrder = false;
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {
                this.updatingOrder = false;
            });
    }
}
